import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LANGS, LANG_CODES, LANG_BY_CODE, ORIGIN, splitPath, pathFor, isTranslatable, useUrlLang, readPref, savePref } from './dgtgLang';
import { useGeo } from '@/lib/useGeo';

// Written in each language, because the dictionary for it has not been loaded on an English page.
const SUGGEST = {
  ar: ['هذه الصفحة متاحة باللغة العربية.', 'اقرأها بالعربية'],
  zh: ['此页面有中文版本。', '阅读中文版'],
  pt: ['Esta página está disponível em português.', 'Ler em português'],
  es: ['Esta página está disponible en español.', 'Leer en español'],
  fr: ['Cette page est disponible en français.', 'Lire en français'],
};
const DISMISS_KEY = 'dgtg_lang_suggest_dismissed';

// Countries where one of our languages is the main one. Used when the browser's own language is not one of
// them, e.g. an English browser in Riyadh or São Paulo. Mixed-language countries (India, Singapore, Canada,
// Switzerland, Belgium) are left out on purpose: a guess there would be wrong for too many readers.
const GEO_LANG = Object.fromEntries([
  ['ar', 'AE SA QA KW BH OM EG JO LB IQ SY YE PS LY SD MA DZ TN MR'],
  ['zh', 'CN TW HK MO'],
  ['pt', 'BR PT AO MZ CV GW ST TL'],
  ['es', 'ES MX AR CO CL PE VE EC GT CU BO DO HN PY SV NI CR PA UY PR GQ'],
  ['fr', 'FR LU MC SN CI CM ML BF NE TG BJ GA CG CD MG GN TD CF HT'],
].flatMap(([lang, cc]) => cc.split(' ').map((c) => [c, lang])));

// hreflang alternates for the six versions of the current page, plus x-default (English).
export function LangAlternates() {
  const { lang, rest } = useUrlLang();
  if (!isTranslatable(rest)) return null;
  const abs = (code) => { const p = pathFor(code, rest, ''); return p === '/' ? ORIGIN : `${ORIGIN}${p}`; };
  return (
    <Helmet>
      <html lang={LANG_BY_CODE[lang]?.hreflang || 'en'} dir={LANG_BY_CODE[lang]?.dir || 'ltr'} />
      {LANGS.map((l) => <link key={l.code} rel="alternate" hrefLang={l.hreflang} href={abs(l.code)} />)}
      <link rel="alternate" hrefLang="x-default" href={abs('en')} />
    </Helmet>
  );
}

// Small language picker for the menu. A native select: keyboard and screen-reader friendly, no library.
export function LangSwitcher({ className = '' }) {
  const { prefix, lang, rest } = useUrlLang();
  const navigate = useNavigate();
  if (!isTranslatable(rest)) return null;
  return (
    <label className={`inline-flex items-center ${className}`}>
      <span className="sr-only">Language</span>
      <select value={lang} onChange={(e) => { savePref(e.target.value); navigate(pathFor(e.target.value, rest, prefix)); }}
        className="bg-transparent text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-2 py-1.5 cursor-pointer"
        data-testid="dgtg-lang-switcher">
        {LANGS.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
      </select>
    </label>
  );
}

// First visit: offer the reader's own language once, in that language. Never redirects anyone who has not
// chosen: Google advises against automatic redirects by location or browser, which also hide pages from its
// crawler. A reader who has chosen a language before is taken to it.
export function LangSuggest() {
  const { prefix, lang, rest } = useUrlLang();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const { country } = useGeo();   // from the CDN's IP-country header; cached for 12 hours

  useEffect(() => {
    setOffer(null);
    if (lang !== 'en' || !isTranslatable(rest)) return;
    const pref = readPref();
    if (pref && pref !== 'en' && LANG_CODES.includes(pref)) { navigate(pathFor(pref, rest, prefix), { replace: true }); return; }
    if (pref) return;
    let dismissed = null;
    try { dismissed = localStorage.getItem(DISMISS_KEY); } catch { /* ignore */ }
    if (dismissed) return;
    const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || '']);
    const code = (langs[0] || '').slice(0, 2).toLowerCase();
    // The reader's own browser language first; if it is not one of ours, the language of the country they are in.
    if (LANG_CODES.includes(code)) setOffer(code);
    else if (GEO_LANG[country]) setOffer(GEO_LANG[country]);
  }, [lang, rest, prefix, navigate, country]);

  if (!offer) return null;
  const [text, cta] = SUGGEST[offer];
  const close = () => { try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ } setOffer(null); };
  return (
    <div className="bg-gray-900 text-white text-sm" dir={LANG_BY_CODE[offer].dir} lang={LANG_BY_CODE[offer].hreflang} data-testid="dgtg-lang-suggest">
      <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-center gap-3 flex-wrap">
        <span>{text}</span>
        <button type="button" className="font-semibold underline underline-offset-4"
          onClick={() => { savePref(offer); navigate(pathFor(offer, rest, prefix)); }}>{cta}</button>
        <button type="button" onClick={close} aria-label="Close" className="text-white/60 hover:text-white text-base leading-none">×</button>
      </div>
    </div>
  );
}

export { splitPath };
